import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Tag,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { deleteObject, getStorage, ref } from "firebase/storage";
import { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import {
  collection,
  db,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
} from "../firebase";
import PortfolioUpload from "./PortfolioUpload";

const PortfolioPanel = ({ adminMode }) => {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    isOpen: isUploadOpen,
    onOpen: onUploadOpen,
    onClose: onUploadClose,
  } = useDisclosure();
  const {
    isOpen: isImageOpen,
    onOpen: onImageOpen,
    onClose: onImageClose,
  } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);

  const toast = useToast();

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    onImageOpen();
  };

  const fetchPortfolio = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "portfolio"));
      const portfolioList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPortfolioItems(portfolioList);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
    }
    setLoading(false);
  };

  /* Function to Delete Portfolio Item & Associated Photos */
  // const handleDelete = async (itemId, photos) => {
  //   if (!window.confirm("Are you sure you want to delete this portfolio item?"))
  //     return;

  //   try {
  //     const storage = getStorage();

  //     // Delete images from Firebase Storage
  //     for (const photoUrl of photos || []) {
  //       const photoRef = ref(storage, photoUrl);
  //       await deleteObject(photoRef).catch((error) => {
  //         console.warn("Error deleting image:", error);
  //       });
  //     }

  //     // Delete document from "portfolio" collection
  //     await deleteDoc(doc(db, "portfolio", itemId));

  //     // Delete related photos from the "photos" dataset in Firestore
  //     const photosQuery = query(
  //       collection(db, "photos"),
  //       where("portfolioId", "==", itemId)
  //     );
  //     const photosSnapshot = await getDocs(photosQuery);

  //     photosSnapshot.forEach(async (photoDoc) => {
  //       await deleteDoc(doc(db, "photos", photoDoc.id));
  //     });

  //     // Update UI after deletion
  //     setPortfolioItems((prev) => prev.filter((item) => item.id !== itemId));

  //     toast({
  //       title: "Portfolio item deleted!",
  //       description:
  //         "The project, associated images, and photos dataset records have been removed.",
  //       status: "success",
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //   } catch (error) {
  //     console.error("Error deleting portfolio item:", error);
  //     toast({
  //       title: "Error",
  //       description: "Could not delete portfolio item.",
  //       status: "error",
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //   }
  // };

  const handleDelete = async (itemId, photos) => {
    if (!window.confirm("Are you sure you want to delete this portfolio item?"))
      return;

    try {
      // Delete document from Firestore
      await deleteDoc(doc(db, "portfolio", itemId));

      // Delete images from Firebase Storage
      const storage = getStorage();

      for (const photoUrl of photos) {
        const photoRef = ref(storage, photoUrl);
        await deleteObject(photoRef).catch((error) => {
          console.warn("Error deleting image:", error);
        });
      }

      // Update UI after deletion
      setPortfolioItems((prev) => prev.filter((item) => item.id !== itemId));

      toast({
        title: "Portfolio item deleted!",
        description: "The project and associated images have been removed.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting portfolio item:", error);
      toast({
        title: "Error",
        description: "Could not delete portfolio item.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box h={{ base: "100%", md: "65vh" }} overflow={"auto"}>
      <HStack justify="space-between" width="100%">
        <Heading my={2} size="md">
          Our Previous Work
        </Heading>
        {adminMode && (
          <Button
            fontWeight="normal"
            size="xs"
            colorScheme="green"
            onClick={onUploadOpen}
            leftIcon={<MdAdd />}
          >
            Add
          </Button>
        )}
      </HStack>
      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mb={8}>
        From electrical installations to home renovations, we have completed
        countless projects. Below, you'll find examples of our past work.
      </Text>
      <PortfolioUpload
        isOpen={isUploadOpen}
        onClose={onUploadClose}
        fetchPortfolio={fetchPortfolio}
      />
      {/* Show Spinner While Loading */}
      {loading ? (
        <Spinner size="lg" color="blue.500" />
      ) : portfolioItems.length === 0 ? (
        <Text fontSize="sm" color="gray.500" textAlign="center">
          No portfolio items available yet. Check back soon!
        </Text>
      ) : (
        <Flex wrap="wrap" spacing={2} width="100%" justify="flex-start">
          {portfolioItems.map((item) => (
            <VStack
              key={item.id}
              bg="gray.50"
              boxShadow="base"
              align="start"
              spacing={2}
              p={2}
              borderRadius="sm"
              border="1px solid"
              borderColor="gray.200"
              //   width={{ base: "100%", md: "48%", lg: "32%" }} // Full width on mobile, grid layout on larger screens
              position="relative"
              minWidth="150px" // Ensures items don’t shrink too much
              m={1} // Adds margin to avoid overlap
            >
              <HStack justifyContent={"space-between"} w={"100%"}>
                <HStack spacing={4} flex={1}>
                  {/* Portfolio Info */}
                  <Heading fontSize={{ base: "xs", md: "sm" }}>
                    {item.name}
                  </Heading>

                  {/* Tags */}
                  <HStack spacing={1} wrap="wrap">
                    {item.tags?.map((tag, i) => (
                      <Tag
                        key={i}
                        size={{ base: "sm", md: "sm" }}
                        fontSize={{ base: "2xs", md: "xs" }}
                        colorScheme="blue"
                        borderRadius={"sm"}
                      >
                        {tag}
                      </Tag>
                    ))}
                  </HStack>
                </HStack>
                {/* 🔥 Delete Button in Top-Right */}
                {adminMode && (
                  <IconButton
                    aria-label="Delete item"
                    icon={<FaTrash fontSize={"10px"} />}
                    colorScheme="red"
                    size="xs"
                    onClick={() => handleDelete(item.id, item.photos)}
                  />
                )}
              </HStack>
              <Divider />

              {/* <HStack spacing={4}> */}
              {/* <VStack align="start">
                  <Text fontSize={{ base: "2xs", md: "sm" }} fontWeight="bold">
                    Photos
                  </Text> */}
              <HStack
                spacing={1}
                overflowX="auto"
                justifyContent={"center"}
                w={"100%"}
              >
                {item.photos?.map((photo, i) => (
                  <Image
                    key={i}
                    src={photo}
                    boxSize={{ base: "55px", md: "140px" }}
                    borderRadius="sm"
                    onClick={() => handleImageClick(photo)}
                    cursor={"pointer"}
                  />
                ))}
              </HStack>
              {/* </VStack> */}
              {/* </HStack> */}
            </VStack>
          ))}
        </Flex>
      )}

      <Modal isOpen={isImageOpen} onClose={onImageClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="white" p={2} borderRadius="sm">
          <ModalCloseButton bg={"black"} color={"white"} />
          {selectedImage && (
            <Image
              src={selectedImage}
              borderRadius="sm"
              maxH="90vh"
              mx="auto"
            />
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PortfolioPanel;
